import { useEffect, useState } from 'react';
import { Card, Table, Tag, Statistic, Row, Col, message } from 'antd';
import { DollarOutlined, TeamOutlined, RiseOutlined } from '@ant-design/icons';
import saApi from '../../services/superAdminApi';
import dayjs from 'dayjs';

interface Revenue {
  total_revenue: number; month_revenue: number;
  active_subscriptions: number; by_plan: Record<string, number>;
}
interface Invoice {
  id: number; invoice_number: string; tenant_name: string; tenant_id: number;
  status: string; amount: number; description: string; paid_at: string; created_at: string;
}

const STATUS_COLORS: Record<string, string> = {
  paid: 'green', pending: 'processing', failed: 'error',
  draft: 'default', refunded: 'orange', cancelled: 'red',
};

export default function SARevenuePage() {
  const [revenue, setRevenue] = useState<Revenue | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      saApi.get('/billing/admin/revenue'),
      saApi.get('/billing/admin/all-invoices'),
    ]).then(([revRes, invRes]) => {
      setRevenue(revRes.data);
      setInvoices(invRes.data.items);
      setLoading(false);
    }).catch(() => { message.error('Ошибка загрузки'); setLoading(false); });
  }, []);

  const columns = [
    { title: '№', dataIndex: 'invoice_number', key: 'num', width: 160 },
    { title: 'Ресторан', dataIndex: 'tenant_name', key: 'tenant' },
    { title: 'Сумма', dataIndex: 'amount', key: 'amount',
      render: (v: number) => `${v.toLocaleString()} ₽` },
    { title: 'Статус', dataIndex: 'status', key: 'status',
      render: (s: string) => <Tag color={STATUS_COLORS[s]}>{s.toUpperCase()}</Tag> },
    { title: 'Описание', dataIndex: 'description', key: 'desc', ellipsis: true },
    { title: 'Дата', dataIndex: 'created_at', key: 'date',
      render: (d: string) => d ? dayjs(d).format('DD.MM.YYYY HH:mm') : '—' },
  ];

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 24 }}>💰 Доход и подписки</h2>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card><Statistic title="Общий доход" value={revenue?.total_revenue || 0}
            prefix={<DollarOutlined />} suffix="₽" precision={0} /></Card>
        </Col>
        <Col span={6}>
          <Card><Statistic title="Доход за месяц" value={revenue?.month_revenue || 0}
            prefix={<RiseOutlined />} suffix="₽" precision={0} valueStyle={{ color: '#3f8600' }} /></Card>
        </Col>
        <Col span={6}>
          <Card><Statistic title="Активных подписок" value={revenue?.active_subscriptions || 0}
            prefix={<TeamOutlined />} /></Card>
        </Col>
        <Col span={6}>
          <Card>
            <div style={{ fontSize: 14, color: '#999' }}>По тарифам</div>
            {revenue?.by_plan && Object.entries(revenue.by_plan).map(([plan, count]) => (
              <Tag key={plan} style={{ margin: 2 }}>{plan}: {count}</Tag>
            ))}
            {(!revenue?.by_plan || Object.keys(revenue.by_plan).length === 0) && <span style={{ color: '#ccc' }}>—</span>}
          </Card>
        </Col>
      </Row>

      <Card title="📋 Все платежи">
        <Table dataSource={invoices} columns={columns} rowKey="id" loading={loading}
          pagination={{ pageSize: 20 }} locale={{ emptyText: 'Нет платежей' }} />
      </Card>
    </div>
  );
}
